import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Apps as AppsIcon,
  Computer as ComputerIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { applicationDefinitionService } from '../services/applicationDefinitionService';
import { healthCheckService } from '../services/healthCheckService';
import { agentService } from '../services/agentService';
import { alertService } from '../services/alertService';
import { formatDateToTR } from '../utils/dateUtils';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles } from '../utils/refreshAnimation';
import type { ApplicationDefinition } from '../types/applicationDefinition';
import type { CurrentHealthStatus } from '../types/healthCheck';
import type { Agent } from '../types/agent';
import type { Alert as AlertType } from '../types/alert';
import { HealthStatus } from '../types/healthCheck';
import { AlertStatus, AlertType as AlertTypeEnum } from '../types/alert';

const ApplicationDefinitions = () => {
  const { selectedTenantIds, selectedServerIds, refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applicationDefinitions, setApplicationDefinitions] = useState<ApplicationDefinition[]>([]);
  const [allApplicationDefinitions, setAllApplicationDefinitions] = useState<ApplicationDefinition[]>([]);
  const [healthStatuses, setHealthStatuses] = useState<Record<string, CurrentHealthStatus>>({});
  const [agents, setAgents] = useState<Agent[]>([]);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // URL parametresinden filtreleri oku
  const statusParam = searchParams.get('status');
  const typeParam = searchParams.get('type');
  const searchParam = searchParams.get('search');
  
  const [filter, setFilter] = useState<{
    search: string;
    status: 'all' | 'healthy' | 'unhealthy' | 'unknown';
    type: 'all' | string;
  }>({
    search: searchParam || '',
    status: (statusParam as 'all' | 'healthy' | 'unhealthy' | 'unknown') || 'all',
    type: typeParam || 'all',
  });
  
  // URL parametresi değiştiğinde filter'ı güncelle
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const typeParam = searchParams.get('type');
    const searchParam = searchParams.get('search');
    
    setFilter({
      search: searchParam || '',
      status: (statusParam as 'all' | 'healthy' | 'unhealthy' | 'unknown') || 'all',
      type: typeParam || 'all',
    });
  }, [searchParams]);

  const loadApplicationDefinitions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const selectedTenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : undefined;
      const selectedServerId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;
      
      const data = await applicationDefinitionService.getAll(selectedTenantId, selectedServerId);
      setAllApplicationDefinitions(data);

      // Agent'ları yükle (agent durumu için gerekli)
      const allAgents = await agentService.getAll();
      setAgents(allAgents);

      // Alert'leri yükle (erişim kontrolü için)
      try {
        const alertsResult = await alertService.getAll({
          tenantId: selectedTenantId,
          serverId: selectedServerId,
          status: undefined, // Tüm durumları getir
          page: 1,
          pageSize: 1000,
        });
        const unresolvedAlerts = (alertsResult.items || []).filter(
          a => a.status !== AlertStatus.Resolved
        );
        setAlerts(unresolvedAlerts);
      } catch (err: any) {
        console.error('[ApplicationDefinitions] Alert\'ler yüklenirken hata:', err);
        setAlerts([]);
      }

      // Her ApplicationDefinition için health status yükle (backend zaten alert'leri dikkate alıyor)
      const statuses: Record<string, CurrentHealthStatus> = {};
      await Promise.all(
        data.map(async (appDef) => {
          try {
            const status = await healthCheckService.getCurrentStatus(appDef.id);
            if (status) {
              statuses[appDef.id] = status;
            }
          } catch (error) {
            console.error('[ApplicationDefinitions] Error loading status for app:', appDef.id, error);
            // Health status yoksa atla
          }
        })
      );
      setHealthStatuses(statuses);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Uygulama tanımları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantIds, selectedServerIds]);
  
  // Filtrelenmiş uygulamaları hesapla
  const getFilteredApplications = useCallback(() => {
    let filtered = [...allApplicationDefinitions];
    
    // Arama filtresi
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      filtered = filtered.filter(
        app => 
          app.name.toLowerCase().includes(searchLower) ||
          app.applicationTypeName?.toLowerCase().includes(searchLower) ||
          app.serverName?.toLowerCase().includes(searchLower) ||
          app.tenantName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Tip filtresi
    if (filter.type !== 'all') {
      const typeNumber = parseInt(filter.type, 10);
      if (!isNaN(typeNumber)) {
        filtered = filtered.filter(app => app.applicationType === typeNumber);
      }
    }
    
    // Status filtresi
    if (filter.status !== 'all') {
      filtered = filtered.filter((app) => {
        const healthStatus = healthStatuses[app.id];
        const status = healthStatus?.latestHealthStatus;
        
        // Erişim kontrolü
        const serverAgents = agents.filter(a => a.serverId === app.serverId);
        const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
        const serverAlerts = alerts.filter(a => 
          a.serverId === app.serverId && 
          a.status !== AlertStatus.Resolved &&
          a.alertType === AlertTypeEnum.AgentHeartbeat
        );
        
        if (!hasOnlineAgent && serverAgents.length > 0) {
          return filter.status === 'unknown';
        }
        if (serverAlerts.length > 0) {
          return filter.status === 'unknown';
        }
        
        // Backend'den gelen status'a göre filtrele
        if (!status) {
          return filter.status === 'unknown';
        }
        
        switch (filter.status) {
          case 'healthy':
            return status === HealthStatus.Healthy;
          case 'unhealthy':
            return status === HealthStatus.Unhealthy;
          case 'unknown':
            return status === HealthStatus.Unknown;
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [allApplicationDefinitions, filter, healthStatuses, agents, alerts]);
  
  const filteredApplications = getFilteredApplications();
  
  // Benzersiz application type'ları bul
  const uniqueApplicationTypes = Array.from(
    new Set(allApplicationDefinitions.map(app => app.applicationType))
  ).map(type => {
    const app = allApplicationDefinitions.find(a => a.applicationType === type);
    return {
      type: String(type), // String olarak sakla (URL parametresi için)
      name: app?.applicationTypeName || String(type),
    };
  });
  
  // Filtre değiştiğinde URL'i güncelle
  const handleFilterChange = (newFilter: Partial<typeof filter>) => {
    const updatedFilter = { ...filter, ...newFilter };
    setFilter(updatedFilter);
    
    const params = new URLSearchParams();
    if (updatedFilter.search) params.set('search', updatedFilter.search);
    if (updatedFilter.status !== 'all') params.set('status', updatedFilter.status);
    if (updatedFilter.type !== 'all') params.set('type', updatedFilter.type);
    
    setSearchParams(params);
  };

  useEffect(() => {
    loadApplicationDefinitions();
  }, [loadApplicationDefinitions, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadApplicationDefinitions();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, loadApplicationDefinitions]);

  const getHealthStatusColor = (appDef: ApplicationDefinition, status?: HealthStatus): 'success' | 'error' | 'warning' | 'default' => {
    // A. Erişim Kontrolü (En Üst Öncelik)
    
    // 1. Server Agent Durumu
    const serverAgents = agents.filter(a => a.serverId === appDef.serverId);
    const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
    
    if (!hasOnlineAgent && serverAgents.length > 0) {
      // Server'ın agent'ı var ama offline - unknown (erişim yok)
      return 'default';
    }
    
    // 2. AgentHeartbeat Alert Kontrolü
    const serverAlerts = alerts.filter(a => 
      a.serverId === appDef.serverId && 
      a.status !== AlertStatus.Resolved &&
      a.alertType === AlertTypeEnum.AgentHeartbeat
    );
    
    if (serverAlerts.length > 0) {
      // AgentHeartbeat alert varsa - unknown (erişim yok)
      return 'default';
    }
    
    // B. Erişim Varsa Backend'den Gelen Status'u Kullan
    // Backend zaten Health Check ve Alert'i birlikte değerlendiriyor
    if (!status) return 'warning';
    switch (status) {
      case HealthStatus.Healthy:
        return 'success';
      case HealthStatus.Unhealthy:
        return 'error';
      case HealthStatus.Unknown:
        return 'warning';
      default:
        return 'warning';
    }
  };

  const getHealthStatusLabel = (appDef: ApplicationDefinition, status?: HealthStatus): string => {
    // A. Erişim Kontrolü (En Üst Öncelik)
    
    // 1. Server Agent Durumu
    const serverAgents = agents.filter(a => a.serverId === appDef.serverId);
    const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
    
    if (!hasOnlineAgent && serverAgents.length > 0) {
      // Server'ın agent'ı var ama offline - unknown (erişim yok)
      return 'Bilinmiyor (Erişim Yok)';
    }
    
    // 2. AgentHeartbeat Alert Kontrolü
    const serverAlerts = alerts.filter(a => 
      a.serverId === appDef.serverId && 
      a.status !== AlertStatus.Resolved &&
      a.alertType === AlertTypeEnum.AgentHeartbeat
    );
    
    if (serverAlerts.length > 0) {
      // AgentHeartbeat alert varsa - unknown (erişim yok)
      return 'Bilinmiyor (Erişim Yok)';
    }
    
    // B. Erişim Varsa Backend'den Gelen Status'u Kullan
    // Backend zaten Health Check ve Alert'i birlikte değerlendiriyor
    if (!status) return 'Bilinmiyor';
    switch (status) {
      case HealthStatus.Healthy:
        return 'Sağlıklı';
      case HealthStatus.Unhealthy:
        return 'Sağlıksız';
      case HealthStatus.Unknown:
        return 'Bilinmiyor';
      default:
        return 'Bilinmiyor';
    }
  };

  const handleViewHistory = (applicationDefinitionId: string) => {
    navigate(`/application-definitions/${applicationDefinitionId}/history`);
  };

  if (loading && applicationDefinitions.length === 0) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Uygulama Tanımları
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtreler */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          ...getRefreshPaperStyles(isRefreshing),
        }}
      >
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Ara..."
            value={filter.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={filter.status}
              label="Durum"
              onChange={(e) => handleFilterChange({ status: e.target.value as any })}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="healthy">Sağlıklı</MenuItem>
              <MenuItem value="unhealthy">Sağlıksız</MenuItem>
              <MenuItem value="unknown">Bilinmiyor</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tip</InputLabel>
            <Select
              value={filter.type}
              label="Tip"
              onChange={(e) => handleFilterChange({ type: e.target.value })}
            >
              <MenuItem value="all">Tümü</MenuItem>
              {uniqueApplicationTypes.map(({ type, name }) => (
                <MenuItem key={type} value={type}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {filteredApplications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', ...getRefreshPaperStyles(isRefreshing) }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {selectedTenantIds.length > 0 || selectedServerIds.length > 0
              ? 'Seçilen kriterlere uygun uygulama tanımı bulunmuyor.'
              : 'Filtre seçerek uygulama tanımlarını görüntüleyebilirsiniz.'}
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <AppsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Uygulama Adı
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Tip</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ComputerIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Sunucu
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Şirket Grubu
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Son Kontrol
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }} align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApplications.map((appDef, index) => {
                  const healthStatus = healthStatuses[appDef.id];
                  return (
                    <TableRow
                      key={appDef.id}
                      hover
                      sx={{
                        bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: 'action.selected' },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <AppsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {appDef.name}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Chip label={appDef.applicationTypeName} size="small" sx={{ height: 22, fontSize: '0.75rem' }} variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {appDef.serverName}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Chip label={appDef.tenantName} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={getHealthStatusLabel(appDef, healthStatus?.latestHealthStatus)}
                          color={getHealthStatusColor(appDef, healthStatus?.latestHealthStatus)}
                          size="small"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {healthStatus?.latestCheckTime ? formatDateToTR(healthStatus.latestCheckTime) : '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }} align="right">
                        <IconButton size="small" onClick={() => handleViewHistory(appDef.id)} color="primary" aria-label="Geçmiş">
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      </Box>
    </Layout>
  );
};

export default ApplicationDefinitions;

